import { StandardEvents } from '@shopify/events';

if (!customElements.get('unum-artwork')) {
  class UnumArtwork extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.closest('form');
      this.file = this.querySelector('input[type=file]');
      this.side = this.querySelector('select');
      this.error = this.querySelector('[data-unum-error]');
      this.fileError = this.error.textContent;
      this.design = this.querySelector('[data-unum-design]');
      this.garment = this.querySelector('[data-unum-garment]');
      this.images = JSON.parse(this.querySelector('[data-unum-images]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-unum-variants]').textContent);
      this.generation = 0;
      this.initEditor(signal);
      this.file.addEventListener('change', () => this.loadFile(), { signal });
      this.side.addEventListener('change', () => this.refresh(), { signal });
      this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, async (event) => {
        const generation = ++this.generation;
        this.pending = true;
        this.previewTicket = {};
        this.previewFailed = false;
        this.querySelector('[data-unum-preview]').hidden = true;
        try {
          const result = await event.promise;
          if (generation !== this.generation) return;
          const variant = result?.detail?.resource;
          if (!variant?.id || !this.variants.some(v => String(v.id) === String(variant.id))) throw new Error();
          this.variantId = String(variant.id);
          this.pending = false;
          this.refresh();
        } catch {
          if (generation !== this.generation) return;
          this.pending = false;
          this.previewFailed = true;
          this.showError(this.dataset.previewError);
        }
      }, { signal });
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.pending || this.decoding || this.garmentPending || this.saving) {
          this.showError(this.dataset.loading);
          return;
        }
        if (this.previewFailed || !this.ready) {
          this.showError(this.dataset.previewError);
          return;
        }
        if (!this.form.reportValidity() || this.form.dataset.unumSubmitting === 'true') return;
        const id = this.form.querySelector('[name=id]');
        if (!id?.value || (this.variantId && id.value !== this.variantId)) {
          this.showError(this.dataset.loading);
          return;
        }
        const revision = this.revision;
        const variantBeforeSave = id.value;
        this.saving = true;
        this.showError(this.dataset.saving);
        try {
          await this.attachPreview();
          if (revision !== this.revision || id.value !== variantBeforeSave || this.pending || this.garmentPending || !this.confirm.checked) throw new Error();
        } catch {
          this.showError(this.dataset.saveError);
          this.saving = false;
          return;
        }
        this.error.hidden = true;
        this.form.enctype = 'multipart/form-data';
        this.form.dataset.unumSubmitting = 'true';
        HTMLFormElement.prototype.submit.call(this.form);
      }, { capture: true, signal });
      window.addEventListener('pageshow', () => { delete this.form.dataset.unumSubmitting; this.saving = false; }, { signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.touchDrag = null;
      this.drag = null;
      this.controller = null;
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }
    initEditor(signal) {
      this.layout = { x: 0.5, y: 0.5, w: 0.7, angle: 0 };
      this.revision = 0;
      this.area = this.querySelector('[data-unum-area]');
      this.handle = this.querySelector('[data-unum-handle]');
      this.confirm = this.querySelector('[data-unum-confirm]');
      this.controls = Object.fromEntries(['size', 'rotation', 'x', 'y'].map(k => [k, this.querySelector(`[data-unum-${k}]`)]));
      for (const [key, input] of Object.entries(this.controls)) {
        input.addEventListener('input', () => {
          const field = { size: 'w', rotation: 'angle', x: 'x', y: 'y' }[key];
          this.layout[field] = Number(input.value) / (key === 'rotation' ? 1 : 100);
          this.updateLayout();
        }, { signal });
      }
      this.querySelector('[data-unum-center]').addEventListener('click', () => {
        this.layout.x = this.layout.y = 0.5;
        this.updateLayout();
      }, { signal });
      this.querySelector('[data-unum-reset]').addEventListener('click', () => {
        this.layout = { x: 0.5, y: 0.5, w: 0.7, angle: 0 };
        this.updateLayout();
      }, { signal });
      this.handle.addEventListener('pointerdown', e => {
        if (!this.ready || e.pointerType === 'touch' || (e.pointerType === 'mouse' && e.button !== 0)) return;
        e.preventDefault();
        this.handle.focus();
        this.handle.setPointerCapture(e.pointerId);
        const rect = this.area.getBoundingClientRect();
        this.drag = { id: e.pointerId, px: e.clientX, py: e.clientY, x: this.layout.x, y: this.layout.y, width: rect.width, height: rect.height };
      }, { signal });
      this.handle.addEventListener('pointermove', e => {
        if (!this.drag || this.drag.id !== e.pointerId) return;
        this.layout.x = this.drag.x + (e.clientX - this.drag.px) / this.drag.width;
        this.layout.y = this.drag.y + (e.clientY - this.drag.py) / this.drag.height;
        this.updateLayout();
      }, { signal });
      for (const event of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        this.handle.addEventListener(event, () => { this.drag = null; }, { signal });
      }
      const stage = this.querySelector('.unum-preview-stage');
      stage.addEventListener('touchstart', e => {
        if (!this.ready || this.pending || this.garmentPending || e.touches.length !== 1) return;
        const touch = e.touches[0];
        const rect = this.area.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        this.drag = null;
        this.touchDrag = { id: touch.identifier, px: touch.clientX, py: touch.clientY, x: this.layout.x, y: this.layout.y, width: rect.width, height: rect.height };
      }, { passive: false, signal });
      document.addEventListener('touchmove', e => {
        if (!this.touchDrag) return;
        if (e.touches.length !== 1) { this.touchDrag = null; return; }
        const touch = Array.from(e.touches).find(t => t.identifier === this.touchDrag.id);
        if (!touch) return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        this.layout.x = this.touchDrag.x + (touch.clientX - this.touchDrag.px) / this.touchDrag.width;
        this.layout.y = this.touchDrag.y + (touch.clientY - this.touchDrag.py) / this.touchDrag.height;
        this.updateLayout();
      }, { passive: false, signal });
      for (const event of ['touchend', 'touchcancel']) {
        document.addEventListener(event, e => {
          if (this.touchDrag && !Array.from(e.touches).some(t => t.identifier === this.touchDrag.id)) this.touchDrag = null;
        }, { signal });
      }
      this.handle.addEventListener('keydown', e => {
        const delta = { ArrowLeft: [-1,0], ArrowRight: [1,0], ArrowUp: [0,-1], ArrowDown: [0,1] }[e.key];
        if (!delta) return;
        e.preventDefault();
        const step = e.shiftKey ? 0.05 : 0.01;
        this.layout.x += delta[0] * step;
        this.layout.y += delta[1] * step;
        this.updateLayout();
      }, { signal });
    }
    invalidate() {
      this.revision++;
      this.confirm.checked = false;
    }
    updateLayout(changed = true) {
      if (!this.ready) return;
      if (changed) this.invalidate();
      const ratio = this.design.naturalWidth / this.design.naturalHeight;
      const areaRatio = 32 / 36;
      const angle = this.layout.angle * Math.PI / 180;
      const c = Math.abs(Math.cos(angle)), s = Math.abs(Math.sin(angle));
      const maxWidth = Math.min(1 / (c + s / ratio), 1 / (areaRatio * (s + c / ratio)));
      this.layout.w = Math.max(0.01, Math.min(this.layout.w, maxWidth, 1));
      const w = this.layout.w, h = w * areaRatio / ratio;
      const bx = (w * c + h / areaRatio * s) / 2;
      const by = (w * areaRatio * s + h * c) / 2;
      this.layout.x = Math.min(1 - bx, Math.max(bx, this.layout.x));
      this.layout.y = Math.min(1 - by, Math.max(by, this.layout.y));
      Object.assign(this.handle.style, { left: `${this.layout.x*100}%`, top: `${this.layout.y*100}%`, width: `${w*100}%`, height: `${h*100}%` });
      this.handle.style.setProperty('--unum-angle', `${this.layout.angle}deg`);
      this.controls.size.value = String(Math.round(w*100));
      this.controls.rotation.value = String(this.layout.angle);
      this.controls.x.value = String(Math.round(this.layout.x*100));
      this.controls.y.value = String(Math.round(this.layout.y*100));
      this.querySelector('[data-unum-size-output]').value = `${Math.round(w*100)}%`;
      this.querySelector('[data-unum-rotation-output]').value = `${this.layout.angle}°`;
      const layout = { version: 2, side: this.side.value, variant: this.variantId || this.form.querySelector('[name=id]')?.value, centerX: +this.layout.x.toFixed(5), centerY: +this.layout.y.toFixed(5), width: +w.toFixed(5), height: +h.toFixed(5), rotation: this.layout.angle, area: { left: .34, top: .29, width: .32, height: .36 }, image: this.garment.getAttribute('src') };
      this.querySelector('[data-unum-layout]').value = JSON.stringify(layout);
      this.querySelector('[data-unum-position]').value = `${this.side.value}; center ${Math.round(this.layout.x*100)}% from left, ${Math.round(this.layout.y*100)}% from top; width ${Math.round(w*100)}% of marked print area; rotation ${this.layout.angle} degrees`;
      this.querySelector('[data-unum-resolution]').hidden = this.design.naturalWidth >= 1200 * w;
    }
    async attachPreview() {
      this.updateLayout(false);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1200;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 1200, 1200);
      const fit = Math.min(1200 / this.garment.naturalWidth, 1200 / this.garment.naturalHeight);
      const gw = this.garment.naturalWidth * fit, gh = this.garment.naturalHeight * fit;
      ctx.drawImage(this.garment, (1200-gw)/2, (1200-gh)/2, gw, gh);
      const { x, y, w, angle } = this.layout;
      const width = 1200 * .32 * w;
      const height = width * this.design.naturalHeight / this.design.naturalWidth;
      ctx.translate(1200 * (.34 + .32*x), 1200 * (.29 + .36*y));
      ctx.rotate(angle * Math.PI / 180);
      ctx.drawImage(this.design, -width/2, -height/2, width, height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error();
      const transfer = new DataTransfer();
      transfer.items.add(new File([blob], `UNUM-${this.side.value.toLowerCase()}-placement.png`, { type: 'image/png' }));
      this.querySelector('[data-unum-preview-file]').files = transfer.files;
    }
    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    async loadFile() {
      const file = this.file.files?.[0];
      this.decoding = false;
      this.garmentPending = false;
      this.previewTicket = {};
      this.ready = false;
      this.querySelector('[data-unum-preview]').hidden = true;
      this.file.setCustomValidity('');
      this.error.hidden = true;
      const ticket = this.fileTicket = {};
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      if (!file) return;
      if (file.size > 20 * 1024 * 1024 || !['image/png', 'image/jpeg'].includes(file.type)) {
        this.file.setCustomValidity(this.fileError);
        this.showError(this.fileError);
        this.file.reportValidity();
        return;
      }
      this.decoding = true;
      const url = this.objectUrl = URL.createObjectURL(file);
      this.design.src = url;
      try {
        await this.design.decode();
        if (ticket !== this.fileTicket) return;
        this.ready = true;
        this.layout = { x: 0.5, y: 0.5, w: 0.7, angle: 0 };
        this.updateLayout();
        this.previewFailed = false;
        this.refresh();
      } catch {
        if (ticket !== this.fileTicket) return;
        this.file.setCustomValidity(this.dataset.previewError);
        this.showError(this.dataset.previewError);
      } finally {
        if (ticket === this.fileTicket) this.decoding = false;
      }
    }
    async refresh() {
      this.touchDrag = null;
      this.drag = null;
      this.invalidate();
      const ticket = this.previewTicket = {};
      this.querySelector('[data-unum-preview]').hidden = true;
      if (!this.ready || !this.side.value || this.pending) return;
      const id = this.variantId || this.form.querySelector('[name=id]')?.value;
      const variant = this.variants.find(v => String(v.id) === String(id));
      const color = variant?.options?.find(option => this.images[option]);
      const src = this.images[color]?.[this.side.value];
      if (!src) {
        this.previewFailed = true;
        this.showError(this.dataset.previewError);
        return;
      }
      this.garmentPending = true;
      this.garment.src = src;
      try {
        await this.garment.decode();
        if (ticket !== this.previewTicket) return;
        this.previewFailed = false;
        this.error.hidden = true;
        this.querySelector('[data-unum-caption]').textContent = `${color} · ${this.side.selectedOptions[0].textContent}`;
        this.querySelector('[data-unum-preview]').hidden = false;
        this.updateLayout(false);
      } catch {
        if (ticket !== this.previewTicket) return;
        this.previewFailed = true;
        this.showError(this.dataset.previewError);
      } finally {
        if (ticket === this.previewTicket) this.garmentPending = false;
      }
    }
  }
  customElements.define('unum-artwork', UnumArtwork);
}

